import { Buffer } from 'buffer'

import { sleep } from '@last/core'

import { useNotifications } from '@/composables/useNotifications'
import SocketFactory from '@/drivers/socket'
import { t } from '@/i18n'
import { CustomError } from '@/utils/custom-error'

import type { CashMachineOptions, ICashMachine } from './types'

class CashKeeper implements ICashMachine {
  private socket: any | null = null
  private resolve: (value: string) => void = () => {}
  private initialized = false
  private charging = false
  private cancelled = false
  private charged = 0
  private config!: {
    ip: string
    port: string
  }
  private inputAmountListener: (amount: number) => void = () => {}
  private totalAmountListener: (amount: number) => void = () => {}

  init({ ip, port, inputAmountListener, totalAmountListener }: CashMachineOptions) {
    if (this.initialized) {
      return
    }
    this.initialized = true
    this.charging = false
    this.cancelled = false
    this.charged = 0
    this.config = { ip, port }
    this.inputAmountListener = inputAmountListener
    this.totalAmountListener = totalAmountListener
  }

  async sendCommand(command: string): Promise<string> {
    if (!this.socket || this.socket?.closed) {
      if (!this.socket) this.socket = SocketFactory.getSocket()
      return new Promise((resolve, reject) => {
        const socket = this.socket
        this.resolve = resolve
        socket.open(
          this.config.ip,
          parseInt(this.config.port),
          () => {
            socket.onData((response: Buffer) => {
              const r = Buffer.from(response).toString()
              if (this.cancelled) {
                if (r.includes('$100|0')) {
                  this.cancelled = false
                }
              } else if (this.charging) {
                if (r.includes('$100|0')) {
                  this.charging = false
                } else if (r.includes('$107')) {
                  this.charged = parseInt(r.split('|')[1])
                } else if (r.includes('$110|37')) {
                  const { notifyError } = useNotifications()
                  notifyError({
                    title: t('cash-machine-status.insufficient-change')
                  })
                  throw new Error('Insufficient change')
                }
              }
              this.resolve(r)
            })
            socket.write(Buffer.from(command), () => {}, reject)
          },
          reject
        )
      })
    } else {
      return new Promise((resolve, reject) => {
        this.resolve = resolve
        this.socket.write(Buffer.from(command), () => {}, reject)
      })
    }
  }

  async initialize() {
    let res = await this.sendCommand('$47#')
    const providedNumber = parseInt(res.split('|')[1])
    const accessNumber = providedNumber + 100001 // FIXME: use number config
    res = await this.sendCommand(`$57|${accessNumber}#`)
    res = await this.sendCommand('$39|0#')
  }

  async charge(amount: number): Promise<number> {
    if (this.cancelled || this.charging) return 0
    if (
      !this.socket ||
      this.socket.closed ||
      (await this.sendCommand(`$42|${amount}|1#`)).includes('$42|0|37')
    ) {
      await this.initialize()
      return this.charge(amount)
    }
    this.charging = true
    this.charged = 0
    while (this.charging && !this.cancelled) {
      this.inputAmountListener(this.charged)
      this.sendCommand('$79|0|0#')
      await sleep(500)
    }
    if (this.cancelled) return 0
    return amount
  }

  async payIn(amount: number) {
    return this.charge(amount)
  }

  async payOut(amount: number): Promise<number> {
    if (!this.socket || this.socket.closed) {
      await this.initialize()
      return this.payOut(amount)
    }
    const res = await this.sendCommand(`$29|${amount}|0#`)
    if (res.includes('$29|0|37')) {
      await this.initialize()
      return this.payOut(amount)
    }
    if (res.includes('$29|0')) {
      const { notifyError } = useNotifications()
      notifyError({
        title: t('cash-machine-status.insufficient-change')
      })
      throw new CustomError('INSUFFICIENT_CHANGE')
    }
    return -amount
  }

  async getCurrentAmount() {
    return this.charged
  }

  async refreshTotalAmount() {
    this.totalAmountListener(await this.getCurrentAmount())
  }

  async cancel() {
    this.cancelled = true
    await this.sendCommand('$42|0|1#')
    while (this.cancelled) {
      this.sendCommand('$79|0|0#')
      await sleep(500)
    }
    this.charging = false
  }
}

export default new CashKeeper()
